import { LANGUAGE_CONFIG } from "@/main/util/LocalizationUtil";
import { primaryLanguageSubtag } from "@/main/util/PublishLanguageMapping";
import type { Localizer } from "@/main/react/Types";

/**
 * The localized NAME of a language, from any locale tag the product stores.
 *
 * `SERIES-7` — the series header printed `mainLocale` raw, so a member reading the page in any of the
 * 25 UI locales was shown the two-letter subtag `en` in a chip row whose other members are complete
 * sentences. That is `AGENTS.md` rule 12 (a BCP-47 subtag is an internal representation) and it is
 * also a translation hole no build guard can see: a string that never enters a catalog is invisible to
 * both key parity and the verbatim guard, which is the `SERIES-3` blind-spot class restated.
 *
 * The idiom is not new — `AddDiscussionToGroupDialog` and `GroupAddDiscussionToGroupDialog` each
 * carried a private `languageLabelFor` doing exactly this. It was lifted here rather than copied a
 * third time, and both dialogs adopted it on 2026-08-28 (their `languageLabelFor` now delegates).
 *
 * Reduce to the PRIMARY SUBTAG before the lookup: what is stored may be `pt-BR`, `en_US` or `EN`, and
 * `LANGUAGE_CONFIG` is keyed by the primary subtag alone.
 *
 * The fallback for a language the app does not configure is the uppercased subtag, matching the two
 * existing call sites byte for byte. Uppercase because it is then visibly a code rather than a word
 * the reader will try to parse. A blank or absent tag returns the empty string, so a caller can render
 * the chip conditionally on the result instead of on the raw input.
 */
export function languageDisplayName(localizer: Localizer, localeTag: string | null | undefined): string {
    const primary = primaryLanguageSubtag(localeTag);
    if (!primary) {
        return "";
    }

    // `keyof typeof LANGUAGE_CONFIG`, not the generated `SeavienLanguageTag`: this module is reachable
    // from a page header, and importing the generated client for a type alone would drag it along.
    // `PublishDraftDialog.tsx:126` indexes the same map the same way.
    const config = LANGUAGE_CONFIG[primary as keyof typeof LANGUAGE_CONFIG];
    return config ? localizer(config.textKey) : primary.toUpperCase();
}
